/**
 * API-Client Foundation für alle IT-SCE Portale.
 *
 * Liefert eine Factory `createApiClient(axios)` die typed Resource-Modules
 * mit einer geteilten Axios-Instance zusammenbaut. Die Axios-Instance kommt
 * aus `useAuth().api` (KeycloakAuthContext) — sie hat bereits Auto-Token-
 * Refresh und Auth-Header-Injection.
 *
 * Naming-Konvention (Sprint-1-Plan, kompatibel mit künftigem
 * `openapi-typescript`-Drop-in in Sprint 8):
 *   - Type-Namen: T<Resource>, T<Resource>List, T<Resource>CreateRequest, ...
 *   - Modul-Namen: passend zu Backend-Route-Prefix (`/api/staff/tokens`
 *     → `tokens.ts`)
 *   - Funktions-Namen: list/get/create/update/delete<Resource>
 *
 * Die Resource-Module sind dünn und tippsicher — keine Business-Logik.
 * Pages konsumieren via `useApiResource` / `useApiMutation` Hooks.
 */

import type { AxiosInstance, AxiosRequestConfig } from 'axios'

/**
 * Standard-Error-Shape aus portal-api. 98% der Handler liefern
 * `{ error: "..." }`. Wir normalisieren in `ApiError`.
 */
export class ApiError extends Error {
  status: number
  data?: unknown

  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Wraps an axios call so callers always get a strongly-typed result
 * or a normalised `ApiError`.
 */
export async function request<T>(
  api: AxiosInstance,
  config: AxiosRequestConfig
): Promise<T> {
  try {
    const res = await api.request<T>(config)
    return res.data
  } catch (e: unknown) {
    const err = e as { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string }
    const status = err.response?.status ?? 0
    const msg =
      err.response?.data?.error ??
      err.response?.data?.message ??
      err.message ??
      'Unbekannter Fehler'
    throw new ApiError(status, msg, err.response?.data)
  }
}

/**
 * Typ für ein Resource-Modul. Jedes Modul (tokens.ts, services.ts, ...)
 * exportiert eine `create<Module>Api(axios)` Factory die ein Objekt mit
 * den Endpoint-Methoden zurückgibt. Diese werden in `createApiClient`
 * zusammengeführt.
 */
export type ResourceModule<T> = (api: AxiosInstance) => T
